'use client'

import { useState } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { ArrowRight, Clock, Shield, Sparkles, Zap } from 'lucide-react'
import { toast } from 'sonner'

import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Container } from '@/components/generals/container'
import { Reveal } from '@/components/generals/reveal'
import { Section, SectionHeader } from '@/components/generals/section'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/components/auth-provider'
import { PricingPlans } from '@/components/pricing-plans'
import { PricingComparisonTable } from '@/components/pricing-comparison-table'

import { singleLogoPrice, unlimitedMonthlyPrice, unlimitedYearlyPrice } from '@/constants/site'

const STRIPE_PUBLISHABLE_KEY = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
const PAYMENTS_CONFIGURED = !!STRIPE_PUBLISHABLE_KEY && STRIPE_PUBLISHABLE_KEY !== 'REPLACE_ME'

const SUBSCRIPTION_BENEFITS = [
    {
        icon: Zap,
        title: 'Unlimited generation',
        body: 'Create as many logos as you need, whenever you need them.'
    },
    {
        icon: Sparkles,
        title: 'Every file format',
        body: 'PNG, SVG, PDF and EPS on every logo, with no per-logo fee.'
    },
    {
        icon: Shield,
        title: 'Private logos',
        body: 'Keep a logo out of the community gallery while you work on it.'
    },
    {
        icon: Clock,
        title: 'No per-logo fee',
        body: 'Design, change the style and design again as often as you like.'
    }
]

const YEARLY_SAVING = unlimitedMonthlyPrice * 12 - unlimitedYearlyPrice
const YEARLY_SAVING_PERCENT = Math.round((YEARLY_SAVING / (unlimitedMonthlyPrice * 12)) * 100)

const FAQS = [
    {
        q: `What do I get for $${singleLogoPrice}?`,
        a: 'Three logo concepts designed from your brief, each with a full-resolution PNG and SVG, PDF and EPS vector files, no watermark and full commercial use. It is one payment, with nothing recurring.'
    },
    {
        q: 'Why do I pay before the logo is designed?',
        a: 'Every design runs on paid image models, so each one is paid for up front. In return the price stays low, every concept arrives already unlocked, and if a design ever fails to finish you are not charged for it.'
    },
    {
        q: 'What if I want to try again?',
        a: `Every new design is another $${singleLogoPrice}, with three new concepts. If you expect to design several, Unlimited at $${unlimitedMonthlyPrice} a month covers every one.`
    },
    {
        q: "What's the difference between Monthly and Yearly?",
        a: `Both include unlimited designs and every file format. Yearly is $${unlimitedYearlyPrice} against $${unlimitedMonthlyPrice * 12} for twelve monthly payments, so it saves $${YEARLY_SAVING} a year.`
    },
    {
        q: 'What happens if I cancel my subscription?',
        a: `You keep unlimited access until the end of the period you paid for. After that you can still make logos at $${singleLogoPrice} each, and every logo you already made stays yours.`
    },
    {
        q: 'Can I change plans later?',
        a: 'Yes. You can switch between monthly and yearly, or cancel, at any time from your account settings.'
    },
    {
        q: "What's the difference between public and private logos?",
        a: 'Logos are shown in the community gallery by default. Subscribers can mark a logo private when they create it so it is never shown publicly.'
    }
]

export default function PricingPage() {
    const router = useRouter()
    const { user } = useAuth()
    const [interval, setInterval] = useState<'monthly' | 'yearly'>('yearly')
    const [loadingPlan, setLoadingPlan] = useState<'logo' | 'paid' | null>(null)

    const isSubscriber = !!user?.isSubscriber

    // A single logo is paid for at the end of the brief, so start the brief.
    const handleBuyLogo = () => {
        router.push('/#hero')
    }

    const handleSubscribe = async (chosen: 'monthly' | 'yearly') => {
        if (loadingPlan) return

        if (!user) {
            router.push(`/login?returnTo=${encodeURIComponent('/pricing')}`)
            return
        }

        if (!PAYMENTS_CONFIGURED) {
            toast.info("Payments aren't set up yet. Please check back soon.")
            return
        }

        try {
            setLoadingPlan('paid')
            const res = await fetch('/api/checkout-subscription', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ interval: chosen })
            })
            const data = await res.json()

            if (res.status === 503) {
                toast.info("Payments aren't set up yet. Please check back soon.")
                return
            }
            if (!res.ok || !data.url) {
                throw new Error(data.error || 'Failed to create checkout session')
            }
            window.location.href = data.url
        } catch (error) {
            toast.error(
                error instanceof Error ? error.message : 'Failed to start checkout. Please try again.'
            )
        } finally {
            setLoadingPlan(null)
        }
    }

    const handleManage = async () => {
        try {
            const res = await fetch('/api/portal-session', { method: 'POST' })
            const data = await res.json()
            if (res.ok && data.url) {
                window.location.href = data.url
                return
            }
            toast.error(data.error || 'Unable to open billing portal.')
        } catch {
            toast.error('Unable to open billing portal.')
        }
    }

    const toggleClass = (active: boolean) =>
        cn(
            'rounded-full px-5 py-2 text-small font-medium transition-colors duration-150 ease-out',
            active ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
        )

    return (
        <>
            <Seo
                title='Pricing'
                description={`$${singleLogoPrice} gets you three logo concepts with every file format. Unlimited is $${unlimitedMonthlyPrice} a month or $${unlimitedYearlyPrice} a year.`}
            />

            {/* Hero */}
            <Section id='pricing-hero' size='md'>
                <Container width='narrow' className='flex flex-col items-center gap-6 text-center'>
                    <h1 className='font-display text-h1 text-balance'>
                        One logo, ${singleLogoPrice}. Every file included.
                    </h1>
                    <p className='text-lead text-ink-muted text-pretty'>
                        Three concepts from your brief, with PNG, SVG, PDF and EPS files and nothing
                        to renew. Making lots of them? Unlimited is ${unlimitedMonthlyPrice} a month.
                    </p>

                    {!PAYMENTS_CONFIGURED && (
                        <p className='w-full rounded-md border border-warning/30 bg-warning-soft px-4 py-3 text-small text-warning'>
                            Payments are not available right now. Please check back shortly.
                        </p>
                    )}

                    <div className='mt-2 flex flex-col items-center gap-3'>
                        <div
                            role='group'
                            aria-label='Subscription billing interval'
                            className='inline-flex items-center rounded-full border border-line bg-surface-2 p-1'
                        >
                            <button
                                type='button'
                                aria-pressed={interval === 'monthly'}
                                onClick={() => setInterval('monthly')}
                                className={toggleClass(interval === 'monthly')}
                            >
                                Monthly
                            </button>
                            <button
                                type='button'
                                aria-pressed={interval === 'yearly'}
                                onClick={() => setInterval('yearly')}
                                className={toggleClass(interval === 'yearly')}
                            >
                                Yearly
                                <span className='ml-1.5 text-success'>Save {YEARLY_SAVING_PERCENT}%</span>
                            </button>
                        </div>
                    </div>
                </Container>
            </Section>

            {/* Plans */}
            <Section id='plans' size='sm'>
                <PricingPlans
                    interval={interval}
                    isSubscriber={isSubscriber}
                    loadingPlan={loadingPlan}
                    onBuyLogo={handleBuyLogo}
                    onSubscribe={() => handleSubscribe(interval)}
                    onManage={handleManage}
                />
            </Section>

            {/* What the subscription adds */}
            <Section id='unlimited-benefits' size='md' tone='sunken' divider>
                <Container width='content'>
                    <SectionHeader
                        title='When a subscription makes more sense'
                        description={`If you are making logos for clients, or iterating all week, $${unlimitedMonthlyPrice} a month beats paying per logo from the third one.`}
                    />

                    <div className='mt-12 grid border-t border-line sm:grid-cols-2'>
                        {SUBSCRIPTION_BENEFITS.map((benefit, index) => (
                            <Reveal
                                key={benefit.title}
                                delay={index * 60}
                                className='flex flex-col gap-3 border-b border-line py-8 sm:odd:pr-10 sm:even:border-l sm:even:pl-10'
                            >
                                <benefit.icon className='size-5 text-ink' aria-hidden />
                                <h3 className='font-display text-h4'>{benefit.title}</h3>
                                <p className='text-small text-ink-muted text-pretty'>
                                    {benefit.body}
                                </p>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </Section>

            {/* Comparison table */}
            <Section id='compare-plans' size='md'>
                <Container width='wide'>
                    <SectionHeader
                        align='center'
                        title='Compare every feature'
                        description='Everything included in each plan, with nothing hidden in a footnote.'
                    />

                    <div className='mt-12'>
                        <PricingComparisonTable
                            highlightPlan='single'
                            onBuyLogo={handleBuyLogo}
                            onMonthlyPlan={() => handleSubscribe('monthly')}
                            onYearlyPlan={() => handleSubscribe('yearly')}
                            userStatus={{
                                isSubscriber,
                                subscriptionStatus: isSubscriber ? 'active' : 'none'
                            }}
                        />
                    </div>
                </Container>
            </Section>

            {/* FAQ */}
            <Section id='pricing-faq' size='md' tone='sunken' divider>
                <Container width='content'>
                    <SectionHeader
                        title='Before you pay anything'
                        description='The things people ask most often about what the price covers.'
                    />

                    <dl className='mt-12 border-t border-line'>
                        {FAQS.map((faq) => (
                            <div
                                key={faq.q}
                                className='grid gap-2 border-b border-line py-7 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] md:gap-10'
                            >
                                <dt className='font-display text-h4 text-balance'>{faq.q}</dt>
                                <dd className='text-body text-ink-muted text-pretty'>{faq.a}</dd>
                            </div>
                        ))}
                    </dl>
                </Container>
            </Section>

            {/* Closing call to action */}
            <Section id='start' size='lg' tone='ink'>
                <Container width='narrow' className='flex flex-col items-center gap-8 text-center'>
                    <h2 className='font-display text-h2 text-balance'>
                        Your logo, in a minute.
                    </h2>
                    <p className='max-w-md text-lead text-pretty opacity-70'>
                        ${singleLogoPrice} for three concepts and every file. Nothing recurring.
                    </p>
                    <Button asChild size='lg' variant='inverse'>
                        <Link href='/#hero'>
                            Start with a sentence <ArrowRight aria-hidden />
                        </Link>
                    </Button>
                </Container>
            </Section>
        </>
    )
}
